// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { AuthenticatedHubConnection } from '@tls/auth/signalr';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import UpdateEmitter from 'components/common/emitter/UpdateEmitter';
import { HistoryInformationMessage } from '@tls/sw91-communication/types/com.base';
import { EarlyStoppingMessage } from '@tls/sw91-communication/types/com.api_number_cruncher';
import {
  CreateContainerProgressMessage,
  CreateContainerCompleteMessage,
} from '@tls/sw91-communication/types/com.api_tai_exporter';
import { isModelEvaluationProgressResult } from 'model/ModelEvaluationProgressResult';
import {
  fetchProjects,
  initializationCompleted,
  initializationStarted,
  updateProjectStatus,
} from 'store/reducers/projectsSlice';
import { isProjectStatusAckMessage } from 'model/reponse/ProjectStatusAckMessage';
import {
  isProjectExportFailedMessage,
  isProjectExportProgressMessage,
} from 'model/reponse/ProjectExportProgressMessage';
import { AppDispatch } from 'store/store';
import { GetAllLabelPackageInfoAckMessage } from '@tls/sw91-communication/types/com.api_db_access';

export const eventEmitter = new UpdateEmitter();

let dispatch: AppDispatch;
export function SetDispatch(newDispatch: AppDispatch) {
  dispatch = newDispatch;
}

let projectsRefetchTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleProjectsRefetch() {
  if (projectsRefetchTimer) {
    clearTimeout(projectsRefetchTimer);
  }
  projectsRefetchTimer = setTimeout(() => {
    projectsRefetchTimer = undefined;
    dispatch(fetchProjects());
  }, 750);
}

// Retry/rebuild/token-refresh/single-flight handling now lives in @tls/auth's AuthenticatedHubConnection.
export const hub = new AuthenticatedHubConnection('/api/live', {
  onGiveUp: () => ToastByStatus(500, 'Live updates', 'Unable to start a live connection'),
});

hub.on('onhistoryinformationupdate', message => {
  eventEmitter.emit('onHistoryInformationUpdate', HistoryInformationMessage.fromJSON(message));
});

hub.on('onearlystopping', message => {
  eventEmitter.emit('onEarlyStopping', EarlyStoppingMessage.fromJSON(message));
});

hub.on('onexportprogress', message => {
  eventEmitter.emit('onExportProgress', CreateContainerProgressMessage.fromJSON(message));
});

hub.on('onexportfailed', message => {
  eventEmitter.emit('onExportFailed', CreateContainerCompleteMessage.fromJSON(message));
});

hub.on('onprojectexportprogress', message => {
  if (!isProjectExportProgressMessage(message)) return;
  eventEmitter.emit('onProjectExportProgress', message);
});

hub.on('onprojectexportfailed', message => {
  if (!isProjectExportFailedMessage(message)) return;
  eventEmitter.emit('onProjectExportFailed', message);
});

hub.on('onevaluationcompleted', message => {
  if (!isModelEvaluationProgressResult(message)) return;
  eventEmitter.emit('onEvaluationCompleted', message);
});

hub.on('onevaluationfailed', message => {
  if (!isModelEvaluationProgressResult(message)) return;
  eventEmitter.emit('onEvaluationFailed', message);
});

hub.on('onqcprogramevaluationprogress', message => {
  eventEmitter.emit('onQCProgramEvaluationProgress', message);
});

hub.on('onqcprogramevaluationcompleted', message => {
  eventEmitter.emit('onQCProgramEvaluationCompleted', message);
});

hub.on('onqcprogramevaluationfailed', message => {
  eventEmitter.emit('onQCProgramEvaluationFailed', message);
});

hub.on('onprojectstatuschanged', message => {
  if (!isProjectStatusAckMessage(message)) return;
  eventEmitter.emit('onProjectStatusChanged', message);
  dispatch(updateProjectStatus(message));
});

hub.on('onprojectinitializationstarted', () => {
  dispatch(initializationStarted());
  scheduleProjectsRefetch();
});

hub.on('onprojectinitialized', () => {
  scheduleProjectsRefetch();
});

hub.on('onprojectinitializationcompleted', () => {
  dispatch(initializationCompleted());
  dispatch(fetchProjects());
});

hub.on('onlabelpackageinfo', message => {
  if (!Array.isArray(message)) return;

  const typedMessage = message.map(GetAllLabelPackageInfoAckMessage.fromJSON);
  if (!typedMessage) return;
  eventEmitter.emit('onLabelPackageInfo', typedMessage);
});
